import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Upload, Sparkles, LayoutGrid, List, Map, MapPin, MapPinOff } from 'lucide-react'
import Popup from 'components/Popup'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import Button from 'components/Button'
import notify from 'components/ui/notify'
import PortfolioLocationsTable from './Portfolios/PortfolioLocationsTable'
import LocationsMapPanel from 'screens/locations/components/Locations/LocationsMapPanel'
import LocationActionSheet from 'screens/locations/components/Locations/LocationActionSheet'
import useIsMobile from 'hooks/useIsMobile'
import { ViewToggle } from 'components/ViewToggle'
import { resolveViewMode } from 'screens/locations/viewMode'
import LocationAddNewCN from 'screens/locations/components/LocationAddNewCN'
import LocationsCards from 'screens/locations/components/Locations/LocationsCards'
import PortfolioLocationRemovePopup from 'components/popups/PortfolioLocationRemovePopup'
import { initializeUsers } from 'slices/usersSlice'
import { addLocationToPortfolio, removeLocationFromPortfolio } from 'slices/portfolioSlice'
import { createLocation, initializeActiveLocations } from 'slices/locationSlice'
import PortfolioAddExistingLocationPopup from '../popups/PortfolioAddExistingLocationPopup'
import PortfolioBulkLoadLocationsPopup from '../popups/PortfolioBulkLoadLocationsPopup'
import styled from 'styled-components'
import PortfolioLocationsEmptyState from 'components/PortfolioLocationsEmptyState'

const Header = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
`

const HeaderTitle = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;

	h2 {
		font-size: 1.5rem;
		font-weight: 700;
		margin: 0;
	}

	span {
		font-size: 0.95rem;
		color: #4a5568;
	}
`

const HeaderActions = styled.div`
	display: flex;
	gap: 0.75rem;
	flex-wrap: wrap;
`

const PortfolioLocationsList = ({ apiKey, userId }) => {
	const { id: portfolioId } = useParams()
	const dispatch = useDispatch()

	const navigate = useNavigate()
	const currentRoute = useLocation()

	const [openAddExistingPopup, setOpenAddExistingPopup] = useState(false)
	const [openCreateNewPopup, setOpenCreateNewPopup] = useState(false)
	const [openLoadListPopup, setOpenLoadListPopup] = useState(false)
	const [openRemovePopup, setOpenRemovePopup] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState(null)
	const [popupTitle, setPopupTitle] = useState(null)
	// The create-location popup widens for its map pane. A 700px map is too
	// cramped to place a pin accurately, which is the point of the pane.
	const [addLocationMode, setAddLocationMode] = useState('form')
	const [viewMode, setViewMode] = useState('table')
	const [mapSheetOpen, setMapSheetOpen] = useState(false)
	const [mapSheetLocation, setMapSheetLocation] = useState(null)

	const { success } = useSelector(state => {
		return {
			success: state.userList.isInitialized,
		}
	})

	const { portfolio, isInitialized } = useSelector(state => {
		return {
			isLoading: state.portfolios.isLoading,
			portfolio: state.portfolios.entities[portfolioId] || {},
			error: state.portfolios.error,
			isInitialized: state.portfolios.isInitialized,
		}
	})

	const ownerId = portfolio?.owner?._id || null
	const isOwner = ownerId === userId
	const portfolioName = portfolio?.assetName || 'Portfolio'

	useEffect(() => {
		dispatch(initializeActiveLocations())
		if (!success) {
			dispatch(initializeUsers())
		}
	}, [dispatch, success])

	var portfolioLocations = []
	if (portfolio.locations) {
		portfolioLocations = portfolio.locations.map(item => {
			return item.location
		})
	}

	const recordCount = portfolioLocations.length
	const records = portfolioLocations

	const _handleRemoveLocation = location => {
		setPopupTitle(`Remove Location`)
		setRecordForEdit(location)
		setOpenRemovePopup(true)
	}

	const locationAdd = async (location, resetForm) => {
		const newLocation = location

		newLocation.owner = userId

		if (newLocation.locationType) {
			const locationType = newLocation.locationType.toLocaleLowerCase()
			const newAssetType = locationType.split(' ')

			if (newAssetType[1]) newLocation.assetType = newAssetType[1]
		}
		const createdLocation = await dispatch(createLocation({ newLocation }))
		const locationId = createdLocation.payload._id
		await dispatch(addLocationToPortfolio({ portfolioId, locationId }))

		dispatch(initializeActiveLocations())
		resetForm()
		setOpenCreateNewPopup(false)
	}

	const addExistingLocationsHandler = () => {
		if (ownerId && userId === ownerId) {
			setPopupTitle(`Add Location to ${portfolioName}`)
			setOpenAddExistingPopup(true)
		} else {
			notify({
				title: `Add Location Error!`,
				text: `You must be the portfolio originator of ${portfolioName} to add a new location.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const createNewLocationsHandler = () => {
		if (ownerId && userId === ownerId) {
			setPopupTitle(`New Location`)
			setAddLocationMode('form')
			setOpenCreateNewPopup(true)
		} else {
			notify({
				title: `Create Location Error!`,
				text: `You must be the portfolio originator of ${portfolioName} to create a new location.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const loadBulkLocationsHandler = () => {
		if (ownerId && userId === ownerId) {
			setPopupTitle(`Add location(s) to ${portfolioName}`)
			setOpenLoadListPopup(true)
		} else {
			notify({
				title: `Add Location Error!`,
				text: `You must be the portfolio originator of ${portfolioName} to add a new location.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const addDropLocationsHandler = () => {
		if (ownerId && userId === ownerId) {
			navigate(`/portfolios/${portfolioId}/addlocations`)
		} else {
			notify({
				title: `Add Location Error!`,
				text: `You must be the portfolio originator of ${portfolioName} to add a new location.`,
				icon: 'error',
				button: 'Ok',
			})
		}
	}

	const handleRemoveLocation = location => {
		dispatch(
			removeLocationFromPortfolio({
				locationId: location._id,
				portfolioId,
			})
		)
		dispatch(initializeActiveLocations())
		setOpenRemovePopup(false)
	}

	// Used by the in-sheet "Remove from Portfolio" override so the destructive
	// button unlinks instead of globally deleting the location.
	const handleRemoveFromPortfolio = async location => {
		if (!location) return
		await dispatch(
			removeLocationFromPortfolio({
				locationId: location._id,
				portfolioId,
			})
		)
		dispatch(initializeActiveLocations())
	}

	const hasLocations = records.length > 0
	const isMobile = useIsMobile()

	// Cards is desktop-only; Map still passes through so the mobile toggle works.
	// State starts at 'table' and is not persisted, so mobile lands on List.
	const effectiveViewMode = resolveViewMode(viewMode, isMobile)

	// Cards and markers both open the location's action sheet.
	const handleSelectMapLocation = location => {
		if (!location) return
		setMapSheetLocation(location)
		setMapSheetOpen(true)
	}

	// Cards is desktop-only, so the option list is filtered rather than the JSX
	// branched. The middle mode was labelled "Table" here and "List" on the
	// Locations index for the same view — aligned to "List".
	const viewOptions = [
		{ value: 'cards', label: 'Cards', icon: LayoutGrid },
		{ value: 'table', label: 'List', icon: List },
		{ value: 'map', label: 'Map', icon: Map },
	].filter(option => !(isMobile && option.value === 'cards'))

	return (
		<div className="space-y-6">
			{hasLocations ? (
				<>
					<Header>
						<HeaderTitle>
							<h2>Locations</h2>
							<span>Track every site in this portfolio and manage updates from a single place.</span>
						</HeaderTitle>
						<div className="flex items-center gap-3 flex-wrap">
							<ViewToggle
								value={effectiveViewMode}
								onChange={setViewMode}
								options={viewOptions}
							/>
							{isOwner && (
								<HeaderActions>
									<Button variant="primary" size="sm" onClick={addExistingLocationsHandler}>
										<span className="flex items-center gap-2">
											<Plus className="w-3.5 h-3.5" />
											Add Existing
										</span>
									</Button>
									<Button variant="primary" size="sm" onClick={createNewLocationsHandler}>
										<span className="flex items-center gap-2">
											<Sparkles className="w-3.5 h-3.5" />
											Create New
										</span>
									</Button>
									<Button variant="ghost" size="sm" onClick={loadBulkLocationsHandler}>
										<span className="flex items-center gap-2">
											<Upload className="w-3.5 h-3.5" />
											Upload List
										</span>
									</Button>
								</HeaderActions>
							)}
						</div>
					</Header>

					{effectiveViewMode === 'map' ? (
						<>
							<LocationsMapPanel
								locations={records}
								onSelectLocation={handleSelectMapLocation}
							/>

							<LocationActionSheet
								location={mapSheetLocation}
								open={mapSheetOpen}
								onClose={() => setMapSheetOpen(false)}
								onDelete={
									mapSheetLocation
										? async () => {
												await handleRemoveFromPortfolio(mapSheetLocation)
										  }
										: undefined
								}
								deleteLabel="Remove from Portfolio"
								deleteConfirmText="Remove this location from the portfolio?"
							/>
						</>
					) : effectiveViewMode === 'cards' ? (
						<LocationsCards
							locations={records}
							userId={userId}
							linkState={{ backButtonTitle: portfolioName, backButtonLink: currentRoute.pathname }}
						/>
					) : (
						<PortfolioLocationsTable
							portfolioName={portfolioName}
							locations={records}
							recordCount={recordCount}
							userId={userId}
							isOwner={isOwner}
							onRemoveFromPortfolio={handleRemoveFromPortfolio}
						/>
					)}
				</>
			) : (
				<PortfolioLocationsEmptyState
					isOwner={isOwner}
					onAddExisting={isOwner ? addExistingLocationsHandler : undefined}
					onCreateNew={isOwner ? createNewLocationsHandler : undefined}
					onUploadList={isOwner ? loadBulkLocationsHandler : undefined}
				/>
			)}

			<Dialog open={openAddExistingPopup} onClose={() => setOpenAddExistingPopup(false)}>
				<DialogContent className="max-w-[800px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<MapPin className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>{popupTitle}</DialogTitle>
								<DialogDescription>Add an existing location to this portfolio</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<PortfolioAddExistingLocationPopup />
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={openCreateNewPopup} onClose={() => setOpenCreateNewPopup(false)}>
				<DialogContent className={addLocationMode === 'map' ? 'max-w-[920px]' : 'max-w-[700px]'}>
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<MapPin className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>{popupTitle}</DialogTitle>
								<DialogDescription>
									Search for a location, or place it on the map, to add to this portfolio
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<LocationAddNewCN locationAdd={locationAdd} onModeChange={setAddLocationMode} />
					</div>
				</DialogContent>
			</Dialog>

			<Popup
				title={popupTitle}
				openPopup={openLoadListPopup}
				setOpenPopup={setOpenLoadListPopup}
				icon={<Upload className="w-5 h-5" />}
				description="Bulk import locations"
			>
				<PortfolioBulkLoadLocationsPopup />
			</Popup>

			<Popup
				title={popupTitle}
				openPopup={openRemovePopup}
				setOpenPopup={setOpenRemovePopup}
				icon={<MapPinOff className="w-5 h-5" />}
				description="Remove location from portfolio"
			>
				<PortfolioLocationRemovePopup
					recordForEdit={recordForEdit}
					handleRemoveLocation={handleRemoveLocation}
					setOpenPopup={setOpenRemovePopup}
				/>
			</Popup>
		</div>
	)
}

export default PortfolioLocationsList
